// The server's message edge (one message from one node to another; the relay is the only writer of its
// state) and everything the row derives from it: the three labels a person sees, the one muted reason
// line, and which edge belongs to which row. Pure: no Svelte, no store.

export type EdgeNodeKind = "person" | "agent" | "session";

export interface EdgeNode {
  kind: EdgeNodeKind;
  /** user id | cybo id | agent(session) id */
  id: string;
  /** Display name the relay resolved when it wrote the edge. */
  name: string;
  daemonId?: string;
  provider?: string;
}

export type EdgeLane = "session_message" | "prompt_session" | "ask_agent";

export type EdgeState =
  | "queued"
  | "accepted"
  | "running"
  | "completed"
  | "replied"
  | "refused"
  | "failed"
  | "expired";

export interface MessageEdge {
  edgeId: string;
  requestId?: string;
  workspaceId: string;
  lane: EdgeLane;
  from: EdgeNode;
  to: EdgeNode;
  state: EdgeState;
  /** Closed code (a2a refusal / orchestration refusal vocabulary). */
  reason?: string;
  chainId?: string;
  hop?: number;
  sentAt: number;
  settledAt?: number;
  reply?: { messageId?: string; at: number };
  /** Your own Forward's answer, on your own fetch only (the relay never broadcasts text). */
  replyText?: string;
}

// ── wire → edge ──────────────────────────────────────────────────────────────

const NODE_KINDS: ReadonlySet<string> = new Set(["person", "agent", "session"]);
const LANES: ReadonlySet<string> = new Set(["session_message", "prompt_session", "ask_agent"]);
// Forward order; the three failures are terminal from any state before `completed`.
const STATE_RANK: Record<EdgeState, number> = {
  queued: 0,
  accepted: 1,
  running: 2,
  completed: 3,
  replied: 4,
  refused: 5,
  failed: 5,
  expired: 5,
};

const rec = (v: unknown): Record<string, unknown> | undefined =>
  typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;
const text = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
const num = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

function nodeOf(v: unknown): EdgeNode | undefined {
  const r = rec(v);
  const kind = text(r?.kind);
  const id = text(r?.id);
  if (!r || !kind || !NODE_KINDS.has(kind) || !id) return undefined;
  const daemonId = text(r.daemonId);
  const provider = text(r.provider);
  return {
    kind: kind as EdgeNodeKind,
    id,
    name: text(r.name) ?? "",
    ...(daemonId && { daemonId }),
    ...(provider && { provider }),
  };
}

const stateOf = (v: unknown): EdgeState | undefined =>
  typeof v === "string" && v in STATE_RANK ? (v as EdgeState) : undefined;
const laneOf = (v: unknown): EdgeLane | undefined =>
  typeof v === "string" && LANES.has(v) ? (v as EdgeLane) : undefined;

function replyOf(v: unknown): MessageEdge["reply"] {
  const r = rec(v);
  const at = num(r?.at);
  if (at === undefined) return undefined;
  const messageId = text(r?.messageId);
  return { at, ...(messageId && { messageId }) };
}

// Absent stays absent: an optional field never arrives as an explicit undefined.
const defined = <T extends object>(o: T): Partial<T> =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;

/**
 * One edge off the wire, or undefined when it is not one this client understands (a state or lane a
 * newer relay added): the row then keeps reading the tool result, exactly as before the edge existed.
 */
export function parseMessageEdge(raw: unknown): MessageEdge | undefined {
  const r = rec(raw) ?? {};
  const edgeId = text(r.edgeId);
  const state = stateOf(r.state);
  const lane = laneOf(r.lane);
  const from = nodeOf(r.from);
  const to = nodeOf(r.to);
  const sentAt = num(r.sentAt);
  if (!edgeId || !state || !lane || !from || !to || sentAt === undefined) return undefined;
  return {
    edgeId,
    workspaceId: text(r.workspaceId) ?? "",
    lane,
    from,
    to,
    state,
    sentAt,
    ...defined({
      requestId: text(r.requestId),
      reason: text(r.reason),
      chainId: text(r.chainId),
      hop: num(r.hop),
      settledAt: num(r.settledAt),
      reply: replyOf(r.reply),
      replyText: text(r.replyText),
    }),
  };
}

/** State only moves forward: a broadcast that arrives late never takes a settled row back. */
export function newerEdge(prev: MessageEdge | undefined, next: MessageEdge): MessageEdge {
  if (!prev) return next;
  // A replied edge only gains its answer's words (your own fetch of your forward).
  if (prev.state === "replied") return next.state === "replied" && next.replyText && !prev.replyText ? next : prev; // prettier-ignore
  if (isEdgeFailure(prev.state)) return prev;
  if (isEdgeFailure(next.state) && prev.state === "completed") return prev;
  return STATE_RANK[next.state] >= STATE_RANK[prev.state] ? next : prev;
}

// ── edge → what a person sees ────────────────────────────────────────────────

export type MessageEdgeLabel = "Sent" | "Replied" | "Not delivered";

export const isEdgeFailure = (state: EdgeState): boolean =>
  state === "refused" || state === "failed" || state === "expired";

/** The ONE mapping from the server's fine state to the three words the row shows. */
export function messageEdgeLabel(state: EdgeState): MessageEdgeLabel {
  if (state === "replied") return "Replied";
  return isEdgeFailure(state) ? "Not delivered" : "Sent";
}

// A message kept for a machine that is away is not "Sent": it has not reached the session yet.
const WAITS_FOR_MACHINE: ReadonlySet<string> = new Set([
  "target_offline",
  "offline",
  "daemon_offline",
]);

/** The pill of an edge-driven row; "Sending…" only while the tool call itself is still running. */
export function messageEdgePill(
  state: EdgeState,
  sending: boolean,
  reason?: string,
): { label: MessageEdgeLabel | "Sending…" | "Waiting"; tone: "muted" | "amber" | "red" } {
  if (isEdgeFailure(state)) return { label: "Not delivered", tone: "red" };
  if (sending) return { label: "Sending…", tone: "muted" };
  if (state === "queued" && reason && WAITS_FOR_MACHINE.has(reason))
    return { label: "Waiting", tone: "amber" };
  return { label: messageEdgeLabel(state), tone: "muted" };
}

export interface EdgeReasonNames {
  /** The other node's name as the row shows it. */
  peer?: string;
  /** The machine the other node runs on, when the viewer can name it. */
  machine?: string;
}

type ReasonLine = (n: Required<EdgeReasonNames>) => string;

// Closed codes -> one line a person can use. Server vocabularies: a2a-refusal.ts A2aRefusalReason,
// session-handoff-chains.ts OrchestrationRefusal, and the ledger's own (message-edge-relay.ts).
const REASON_LINES: Record<string, ReasonLine> = {
  busy: (n) => `${n.peer} is busy. Your message is next.`,
  offline: (n) => `${n.machine} is offline. Delivers when it is back.`,
  daemon_offline: (n) => `${n.machine} is offline. Delivers when it is back.`,
  archived: () => "That session is archived.",
  not_found: () => "That session no longer exists.",
  unresolvable: () => "That agent belongs to another workspace.",
  queue_full: (n) => `${n.peer} has too many messages waiting.`,
  expired: (n) => `${n.machine} did not come back in time.`,
  depth: () => "This chain of agent calls hit its limit.",
  chain_budget: () => "This chain of agent calls hit its limit.",
  depth_exhausted: () => "This chain of agent calls hit its limit.",
  claim_denied: () => "That agent was already called in this chain.",
  thread_turn_budget: () => "This thread's agent turns are spent until a person posts.",
  fanout_capped: () => "Too many agents were mentioned at once.",
  chain_unknown: () => "Its chain of agent calls could not be checked.",
  rate: () => "Too many messages in a short time.",
  permission: () => "You do not have access to that session.",
  source: () => "You do not have access to this session's machine.",
  no_actor: () => "There is no person to run it for.",
  no_initiator: () => "There is no person to run it for.",
  no_pro: () => "Requires Pro.",
  not_member: () => "That agent is not a member of this channel.",
  channel_missing: () => "The channel no longer exists.",
  turn_failed: (n) => `${n.peer} could not finish its turn.`,
  // Stage 2: why a message still waits (the edge is queued), and what its delivery found.
  target_busy: (n) => `${n.peer} is busy. Your message is next.`,
  target_offline: (n) => `${n.machine} is offline. Delivers when it is back.`,
  target_usage_limit: (n) => `${n.peer} ran into its usage limit.`,
  unavailable: (n) => `${n.peer} could not take it.`,
  session_gone: () => "That session no longer exists.",
  owner_moved: () => "That session moved to another machine.",
  not_owner: () => "You no longer have access to that session.",
  no_access: () => "You no longer have access to that session.",
  user_gone: () => "There is no person to run it for.",
  payload_gone: () => "It could not be delivered.",
  payload_unreadable: () => "It could not be delivered.",
  error: () => "It could not be delivered.",
  self: () => "That is this same session.",
  // Stages 3 and 4: an agent's machine, and another person's session.
  target_outdated: (n) => `${n.machine} needs an update before it can take this.`,
  agent_not_started: (n) => `${n.peer} could not be started.`,
  contained_unavailable: (n) =>
    `${n.peer} runs on a machine you cannot use, and cannot take it safely.`,
};

// Stage 5: a code whose words depend on the lane. On a direct message (cyborg7_message,
// cyborg7_prompt_session) the fan-out limit is the sender's own turn, and nobody was mentioned.
const DIRECT_LANES: ReadonlySet<string> = new Set(["session_message", "prompt_session"]);
const DIRECT_LINES: Record<string, ReasonLine> = {
  // No longer sent (Fab, 2026-10-09: no limit per step); kept for rows written before.
  fanout_capped: () => "Too many messages were sent at once. This one was not sent.",
  // The 200-message loop-breaker: agents kept messaging each other.
  chain_budget: () => "Stopped: agents kept messaging each other in a loop.",
  chain_unknown: () => "Its chain of agent messages could not be checked.",
  // A name the sender gave that matches nothing (often mistyped), not a session that went away.
  not_found: () => "No session or agent goes by that name.",
  // The one approval card of the sending session (server message-approval-group.ts): no edge.
  approval_denied: () => "Not sent: messaging was not allowed for this session.",
  approval_expired: () => "Not sent: nobody allowed it in time.",
};

// A machine that is offline is a wait while the edge is still open, and the reason it failed once it is not.
const FAILED_LINES: Record<string, ReasonLine> = {
  offline: (n) => `${n.machine} is offline.`,
  daemon_offline: (n) => `${n.machine} is offline.`,
  busy: (n) => `${n.peer} is busy.`,
  // An expired edge keeps the reason it was waiting for.
  target_offline: (n) => `${n.machine} did not come back in time.`,
  target_busy: (n) => `${n.peer} stayed busy for too long.`,
  // Its person stopped the target's turn before it answered (Fab, 2026-10-09).
  turn_canceled: (n) => `${n.peer}'s turn was stopped before it answered.`,
};

/**
 * The one muted line an expanded row shows, only when it adds something: undefined for a code this
 * client has no words for (never the raw code) and for a settled row that needs no explanation.
 */
export function messageEdgeReason(
  edge: Pick<MessageEdge, "state" | "reason"> & Partial<Pick<MessageEdge, "lane">>,
  names: EdgeReasonNames = {},
): string | undefined {
  if (edge.state === "replied" || edge.state === "completed") return undefined;
  const code = edge.reason ?? (edge.state === "expired" ? "expired" : undefined);
  if (!code) return undefined;
  const line =
    (isEdgeFailure(edge.state) ? FAILED_LINES[code] : undefined) ??
    (edge.lane && DIRECT_LANES.has(edge.lane) ? DIRECT_LINES[code] : undefined) ??
    REASON_LINES[code];
  return line?.({
    peer: names.peer || "That session",
    machine: names.machine || "Its machine",
  });
}

/**
 * The same line for a refusal the relay recorded NO edge for (a name it could not resolve, the
 * session itself): the tool's own text opens "Error (<code>):". Undefined for anything else, and for
 * a code with no line, so the caller keeps what it showed before.
 */
export function refusalTextReason(
  refusal: string | undefined,
  lane: MessageEdge["lane"] | undefined,
  names: EdgeReasonNames = {},
): string | undefined {
  const code = /^\s*Error \(([a-z_]+)\):/.exec(refusal ?? "")?.[1];
  return code ? messageEdgeReason({ state: "refused", reason: code, lane }, names) : undefined;
}

/** Provider chip rule: the other node's provider, only when it is known and differs from the sender's. */
export function edgeProviderChip(
  peer: string | undefined,
  sender: string | undefined,
  labels: Readonly<Record<string, string>> = {},
): string | undefined {
  if (!peer || !sender || peer === sender) return undefined;
  return labels[peer] ?? peer;
}

// ── which edge is which row ──────────────────────────────────────────────────

/** What a sent row knows about itself (a PeerMessage has all of it). */
export interface EdgeRowKey {
  lane?: EdgeLane;
  /** The edge id the tool result names: the only exact join. */
  edgeId?: string;
  chainId?: string;
  askId?: string;
  threadId?: string;
  target: { id?: string; name?: string };
  at?: number;
}

// A row with no id to match on (a refused hand-off names no chain) takes an edge only by time, and
// only when exactly one edge and one row could be each other.
const NEAR_MS = 2 * 60 * 1000;

function strongMatch(row: EdgeRowKey, e: MessageEdge): boolean {
  if (row.askId && (e.edgeId === row.askId || e.requestId === row.askId)) return true;
  if (!row.chainId || e.chainId !== row.chainId) return false;
  return !row.target.id || e.to.id === row.target.id;
}

const near = (row: EdgeRowKey, e: MessageEdge): boolean =>
  row.at !== undefined && Math.abs(e.sentAt - row.at) <= NEAR_MS && mayBeTarget(row, e);

// A row that names its target joins no edge to another one (a refused ask has no edge of its own).
function mayBeTarget(row: EdgeRowKey, e: MessageEdge): boolean {
  if (row.target.id) return row.target.id === e.to.id;
  const name = row.target.name?.trim().toLowerCase();
  if (!name || !e.to.name) return true;
  // the caller's words: the target's name, its id, or the start of its id
  return (
    name === e.to.name.toLowerCase() || (name.length >= 8 && e.to.id.toLowerCase().startsWith(name))
  );
}

/**
 * callId -> the edge of that sent row. Only edges this session sent (`selfIds`: its agent id and, for an
 * agent, its cybo id), only rows of a Cyborg lane (Claude's native SendMessage has no edge), each edge
 * for one row: never a guess.
 */
export function joinEdgesToRows(
  rows: Iterable<readonly [string, EdgeRowKey]>,
  edges: readonly MessageEdge[],
  selfIds: ReadonlySet<string>,
): Map<string, MessageEdge> {
  const sent = edges.filter((e) => selfIds.has(e.from.id)).sort((a, b) => a.sentAt - b.sentAt);
  const out = new Map<string, MessageEdge>();
  if (sent.length === 0) return out;
  const open = new Set(sent);
  const list = [...rows].filter(([, r]) => r.lane !== undefined);
  // A row that names its edge takes that edge and no other: chain and hop are shared by every send of a turn.
  for (const [id, row] of list) {
    if (!row.edgeId) continue;
    const edge = sent.find((e) => open.has(e) && e.edgeId === row.edgeId);
    if (!edge) continue;
    out.set(id, edge);
    open.delete(edge);
  }
  // One chain id covers every hop: several sends to the same session in it pair oldest to oldest.
  for (const [id, row] of list) {
    if (row.edgeId) continue;
    const edge = sent.find((e) => open.has(e) && e.lane === row.lane && strongMatch(row, e));
    if (!edge) continue;
    out.set(id, edge);
    open.delete(edge);
  }
  const loose = list.filter(([id, r]) => !out.has(id) && !r.edgeId && !r.chainId && !r.askId);
  for (const [id, row] of loose) {
    const fits = (e: MessageEdge) => open.has(e) && e.lane === row.lane && near(row, e);
    const candidates = sent.filter(fits);
    if (candidates.length !== 1) continue;
    const rivals = loose.filter(([, r]) => r.lane === row.lane && near(r, candidates[0]));
    if (rivals.length === 1) out.set(id, candidates[0]);
  }
  for (const e of out.values()) open.delete(e);
  return out;
}

/** The edge of a row this session RECEIVED: same chain, from that sender, the one sent closest to the row. */
export function edgeOfReceivedRow(
  row: { chainId?: string; fromSessionId?: string; at?: number },
  edges: readonly MessageEdge[],
  selfIds: ReadonlySet<string>,
): MessageEdge | undefined {
  if (!row.chainId || !row.fromSessionId) return undefined;
  const hits = edges.filter(
    (e) => selfIds.has(e.to.id) && e.chainId === row.chainId && e.from.id === row.fromSessionId,
  );
  if (hits.length < 2 || row.at === undefined) return hits[0];
  const at = row.at;
  return hits.reduce((best, e) =>
    Math.abs(e.sentAt - at) < Math.abs(best.sentAt - at) ? e : best,
  );
}
